// src/rms/data/messageService.js
// ============================================================================
// Talking to customers
// ============================================================================
// The browser never sends email. It asks the send-message edge function to,
// because that function holds the Resend key and this file ships to every
// visitor. Everything here is either a read through the normal adapter or a
// call to that function.
//
// Templates are per-operator rows seeded from the vertical preset, so a marina
// and a storage yard say different things without either of them being a code
// branch.
// ============================================================================

import db from "./index";
import { isLocalBackend } from "./index";
import { supabase } from "../config/supabaseClient";
import { createSuccessResponse, createErrorResponse, isSuccess } from "../utils/response";

/**
 * Substitutes {{placeholders}}.
 *
 * An unknown placeholder is left visible rather than replaced with an empty
 * string: "Your booking starts on " with nothing after it looks like a bug the
 * customer has to interpret, whereas "{{starts_at}}" is obviously a template
 * the operator needs to fix.
 */
export function fillTemplate(text, values) {
  return String(text || "").replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (whole, key) => {
    const value = values[key];
    return value === undefined || value === null || value === "" ? whole : String(value);
  });
}

/** Placeholders offered in the template editor, so nobody has to guess. */
export const TEMPLATE_TOKENS = [
  { key: "customer_name", hint: "The renter's first name" },
  { key: "business_name", hint: "Your business" },
  { key: "asset", hint: "Unit number and name, e.g. CAR-014 — Toyota Corolla" },
  { key: "starts_at", hint: "Pickup date and time" },
  { key: "ends_at", hint: "Return date and time" },
  { key: "reservation_number", hint: "e.g. RES-0042" },
  { key: "total", hint: "Total charge, formatted" },
  { key: "balance", hint: "Outstanding balance, formatted" },
];

// ----------------------------------------------------------------------------
// Threads
// ----------------------------------------------------------------------------

export async function listThreads(operatorId) {
  return db.list("messageThreads", { operator_id: operatorId }, {
    orderBy: "last_message_at",
    direction: "desc",
  });
}

export async function listMessages(threadId) {
  return db.list("messages", { thread_id: threadId }, { orderBy: "created_at" });
}

export async function markThreadRead(threadId) {
  if (isLocalBackend) {
    await db.update("messageThreads", threadId, { unread_count: 0 });
    return createSuccessResponse(true);
  }
  const { error } = await supabase.rpc("rms_mark_thread_read", { p_thread_id: threadId });
  return error ? createErrorResponse(error.message) : createSuccessResponse(true);
}

// ----------------------------------------------------------------------------
// Sending
// ----------------------------------------------------------------------------

/**
 * Sends a message to a customer.
 *
 * @returns {Promise<{success, data: {thread_id}, error}>}
 */
export async function sendMessage({
  operatorId,
  customerId,
  threadId = null,
  reservationId = null,
  subject,
  body,
  templateKey = null,
}) {
  if (!body?.trim()) return createErrorResponse("Write something first.");

  // On the local backend there is no edge function and no email. Rather than
  // fake a send, the message is recorded and clearly marked — so the thread UI
  // can be built and tested without a mail provider attached.
  if (isLocalBackend) {
    let thread = threadId ? (await db.get("messageThreads", threadId)).data : null;

    if (!thread) {
      thread = (await db.create("messageThreads", {
        operator_id: operatorId,
        customer_id: customerId,
        reservation_id: reservationId,
        subject: subject || "Your rental",
        status: "open",
        unread_count: 0,
        last_message_at: new Date().toISOString(),
      })).data;
    }

    await db.create("messages", {
      operator_id: operatorId,
      thread_id: thread.id,
      direction: "outbound",
      to_email: null,
      subject: subject || thread.subject,
      body_text: body,
      template_key: templateKey,
      delivery_status: "queued",
      delivery_error: "Local backend — no email was actually sent.",
    });

    await db.update("messageThreads", thread.id, { last_message_at: new Date().toISOString() });
    return createSuccessResponse({ thread_id: thread.id, local_only: true });
  }

  const { data, error } = await supabase.functions.invoke("send-message", {
    body: {
      operator_id: operatorId,
      customer_id: customerId,
      thread_id: threadId,
      reservation_id: reservationId,
      subject,
      body,
      template_key: templateKey,
    },
  });

  // The function returns a useful sentence in `error`; surface that rather than
  // the generic "Edge Function returned a non-2xx status code".
  if (error) {
    const detail = data?.error || (await readFunctionError(error));
    return createErrorResponse(detail || error.message);
  }
  if (data?.error) return createErrorResponse(data.error);

  return createSuccessResponse(data);
}

/** supabase-js hides the response body on non-2xx; dig it back out. */
async function readFunctionError(error) {
  try {
    const body = await error.context?.json?.();
    return body?.error || null;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------------
// Templates
// ----------------------------------------------------------------------------

export async function listTemplates(operatorId) {
  return db.list("messageTemplates", { operator_id: operatorId }, { orderBy: "template_key" });
}

/**
 * Sends one of the automatic messages.
 *
 * Silently does nothing when the template is switched off — an operator who
 * turned off pickup reminders should not get an error every time a booking is
 * confirmed. Returns a reason so callers can log it if they care.
 */
export async function sendTemplated({ operatorId, templateKey, customer, values, reservationId }) {
  const templates = await listTemplates(operatorId);
  if (!isSuccess(templates)) return createErrorResponse(templates.error);

  const template = (templates.data || []).find((t) => t.template_key === templateKey);
  if (!template) return createSuccessResponse({ skipped: "no template" });
  if (!template.is_active) return createSuccessResponse({ skipped: "template off" });
  if (!customer?.email) return createSuccessResponse({ skipped: "customer has no email" });

  return sendMessage({
    operatorId,
    customerId: customer.id,
    reservationId,
    subject: fillTemplate(template.subject, values),
    body: fillTemplate(template.body, values),
    templateKey,
  });
}

export default {
  listThreads,
  listMessages,
  markThreadRead,
  sendMessage,
  sendTemplated,
  listTemplates,
  fillTemplate,
  TEMPLATE_TOKENS,
};
